import { Wallet, ShieldCheck, Bell, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";
import LandingPhone from "./LandingPhone";
import Reveal from "../Reveal";

const points = [
  {
    Icon: Wallet,
    title: "Unified wallets",
    desc: "Spot and INR balances in one place with deposit whitelisting.",
  },
  {
    Icon: ShieldCheck,
    title: "Security first",
    desc: "2FA, session controls, and KYC gates before high-risk actions.",
  },
  {
    Icon: Bell,
    title: "Real-time alerts",
    desc: "Order fills and withdrawal status — never miss a move.",
  },
  {
    Icon: Smartphone,
    title: "Web + Android",
    desc: "Trade in the browser today; install the APK from our download page.",
  },
];

function PointCard({ Icon, title, desc }) {
  return (
    <div
      className="bg-white rounded-[16px] p-5 border border-[var(--cm-border)] shadow-[0_20px_40px_-18px_rgba(29,45,63,0.14)] cm-hover-lift"
      data-testid={`mp-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <span className="shrink-0 w-9 h-9 rounded-xl bg-cm-blue grid place-items-center text-white">
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <h4 className="text-[15px] font-bold text-cm-navy">{title}</h4>
      </div>
      <p className="text-[13px] text-cm-grey leading-[1.65]">{desc}</p>
    </div>
  );
}

export default function MultiplePossibilities() {
  return (
    <section
      className="cm-landing-section bg-cm-surface border-y border-[var(--cm-border)] relative overflow-hidden"
      data-testid="multiple-possibilities"
    >
      <div className="absolute inset-0 cm-landing-grid-bg opacity-30 pointer-events-none" />
      <div className="cm-container relative">
        <Reveal variant="fade-up" className="text-center mb-12 lg:mb-14 max-w-[640px] mx-auto">
          <div className="cm-landing-section-label mb-4">Mobile & web</div>
          <h2 className="cm-landing-section-title">One application — multiple possibilities</h2>
          <p className="mt-4 text-[15px] text-cm-grey leading-[1.75]">
            Manage portfolios, execute trades, and stay compliant from any device — without
            switching tools.
          </p>
        </Reveal>

        {/* Desktop: orbit layout */}
        <div className="hidden lg:block relative max-w-[1100px] mx-auto h-[680px]">
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="relative cm-spin-slow" style={{ width: "580px", height: "580px" }}>
              <div
                className="absolute inset-0 rounded-full border cm-ring-breathe"
                style={{ borderColor: "rgba(2,44,237,0.2)" }}
              />
              <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-cm-blue cm-orbit-pulse" />
            </div>
          </div>

          <div className="absolute inset-0 grid place-items-center">
            <Reveal variant="zoom-soft" duration={1100}>
              <LandingPhone
                variant={1}
                alt="Holdings screen"
                className="w-[520px] max-w-full h-auto relative z-10 cm-float"
                testId="mp-phone"
              />
            </Reveal>
          </div>

          <Reveal variant="fade-right" delay={200} className="absolute top-[120px] left-0 w-[240px]">
            <PointCard {...points[0]} />
          </Reveal>
          <Reveal variant="fade-left" delay={300} className="absolute top-[120px] right-0 w-[240px]">
            <PointCard {...points[1]} />
          </Reveal>
          <Reveal variant="fade-right" delay={400} className="absolute bottom-[100px] left-0 w-[240px]">
            <PointCard {...points[2]} />
          </Reveal>
          <Reveal variant="fade-left" delay={500} className="absolute bottom-[100px] right-0 w-[240px]">
            <PointCard {...points[3]} />
          </Reveal>
        </div>

        {/* Mobile / tablet: stacked */}
        <div className="lg:hidden flex flex-col items-center gap-8">
          <Reveal variant="zoom-soft">
            <LandingPhone
              variant={1}
              alt="Holdings screen"
              className="w-full max-w-[320px] h-auto"
              testId="mp-phone-mobile"
            />
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-4 w-full max-w-[560px]">
            {points.map((p, i) => (
              <Reveal key={p.title} variant="fade-up" delay={i * 80}>
                <PointCard {...p} />
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal variant="fade-up" delay={200} className="mt-12 text-center">
          <Link
            to="/download"
            className="inline-flex items-center justify-center h-[48px] px-8 rounded-full bg-cm-blue text-white font-bold text-[14px] hover:bg-cm-blue-deep transition"
            data-testid="mp-download-cta"
          >
            Get the Android app
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
